import { AxiosError } from 'axios';
import React, {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { handleApiErrors } from '../api/apiErrorHandler';
import { apiUrl } from '../api/apiUrl';
import http from '../api/axios';
import useLocalStorage from '../hooks/useLocalStorage';
import { ApplicationUser } from '../types/ApplicationUser';
import { localStorageKeys } from '../utils/keys/localStorageKeys';
import logger from '../utils/logger';

interface AuthContextType {
  user: ApplicationUser | null;
  isAuthenticated: boolean;
  login: (token: string) => void;
  logout: () => void;
  refreshUser: () => void;
}

const _initialContextData: AuthContextType = {
  user: null,
  isAuthenticated: false,
  login: () => null,
  logout: () => null,
  refreshUser: () => null,
};

export const AuthContext = createContext<AuthContextType>(_initialContextData);

/**
 * AuthProvider for global user management
 */
function AuthProvider({ children }: PropsWithChildren) {
  const [storedUser, setStoredUser] = useLocalStorage<ApplicationUser | null>(
    localStorageKeys.USER_KEY,
    null,
  );
  const [storedToken, setStoredToken] = useLocalStorage<string | null>(
    localStorageKeys.BEARER_TOKEN_KEY,
    null,
  );
  const [user, setUser] = useState<ApplicationUser | null>(storedUser);
  const [isAuthenticated, setAuthenticated] = useState<boolean>(storedUser ? true : false);

  useEffect(() => {
    if (storedToken) {
      revalidateToken();
    }
    logger.i({ storedUser, user, isAuthenticated });
  }, []);

  const revalidateToken = async () => {
    logger.i(storedToken);
    try {
      const response = await http.apiGet({ url: apiUrl.GENERATE_NEW_TOKEN_FOR_CURRENT_USER });
      if (response?.status === 200) {
        const { token } = response.data;
        setStoredToken(token);
      }
    } catch (err) {
      if (err instanceof AxiosError) {
        if (err.response?.status === 401) {
          logout();
        }
        handleApiErrors(err);
      } else {
        logger.e(err);
      }
    }
  };

  /**
   * Set user to local storage / refresh data
   */
  const refreshUser = async () => {
    const response = await http.apiGet({ url: apiUrl.GET_CURRENT_USER });
    if (response?.status === 200) {
      const { data } = response;
      setStoredUser(data);
      setUser(data);
      setAuthenticated(true);
    }
  };

  const login = useCallback(async (token: string) => {
    setStoredToken(token);
    try {
      refreshUser();
    } catch (err) {
      handleApiErrors(err);
    }
  }, []);

  const logout = useCallback(() => {
    setUser(null);
    setAuthenticated(false);
    localStorage.clear();
  }, []);

  return (
    <AuthContext.Provider value={{ user, isAuthenticated, login, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;

export const useAuth = () => {
  return useContext(AuthContext);
};
